import React, { useEffect, useState } from 'react';
import './AccountSettingsPage.css';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  getDocs,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { updatePseudoInTournaments } from '../utils/profileUtils';
import GoogleAuthenticatorSettings from '../components/GoogleAuthenticatorSettings';
import SettingsIcon from '../components/SettingsIcon';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { validatePassword, shouldEnforcePasswordPolicy, generateSecurePassword } from '../utils/passwordPolicy';
import { validatePhoneNumberUniqueness, validatePhoneNumberFormat, normalizePhoneNumber } from '../utils/phoneValidation';
import PhoneVerification from '../components/PhoneVerification';

const AccountSettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('basic');
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [pseudo, setPseudo] = useState('');
  const [facebookLink, setFacebookLink] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [masterDuelId, setMasterDuelId] = useState('');
  const [edoproPseudo, setEdoproPseudo] = useState('');
  const [statsVisibility, setStatsVisibility] = useState({
    matches: true,
    wins: true,
    losses: true,
    champion: true,
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [itemPurchaseHistory, setItemPurchaseHistory] = useState([]);
  
  // Pagination states
  const [dpPurchasePage, setDpPurchasePage] = useState(1);
  const [itemPurchasePage, setItemPurchasePage] = useState(1);
  const itemsPerPage = 6;
  
  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Password policy states
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  
  
  
  // Phone number validation states
  const [phoneValidation, setPhoneValidation] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  
  // Phone edit modal states
  const [showPhoneEditModal, setShowPhoneEditModal] = useState(false);
  const [phoneEditStep, setPhoneEditStep] = useState('edit'); // 'edit' or 'confirm'
  const [tempPhoneNumber, setTempPhoneNumber] = useState('');

  // Pagination helper functions
  const getPaginatedData = (data, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data, itemsPerPage) => {
    return Math.ceil(data.length / itemsPerPage);
  };

  const resetPagination = () => {
    setDpPurchasePage(1);
    setItemPurchasePage(1);
  };

  // Password validation functions
  const validateNewPassword = (password) => {
    if (!password) {
      setPasswordValidation(null);
      return;
    }

    const validation = validatePassword(password, userEmail, userData?.pseudo);
    setPasswordValidation(validation);
    return validation;
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    validateNewPassword(password);
  };

  const generateNewPassword = () => {
    const newPassword = generateSecurePassword(12);
    setGeneratedPassword(newPassword);
    setNewPassword(newPassword);
    validateNewPassword(newPassword);
    setShowPasswordGenerator(false);
  };

  const useGeneratedPassword = () => {
    setNewPassword(generatedPassword);
    validateNewPassword(generatedPassword);
    setShowPasswordGenerator(false);
  };

  // Phone number validation functions
  const validatePhoneNumber = async (phone) => {
    if (!phone || !phone.trim()) {
      setPhoneValidation(null);
      setPhoneError('');
      return;
    }

    // First check format
    if (!validatePhoneNumberFormat(phone)) {
      setPhoneValidation({ isValid: false, message: 'Invalid phone number format. Use format: 0341234567' });
      setPhoneError('Invalid phone number format');
      return;
    }

    // Then check uniqueness
    const auth = getAuth();
    const user = auth.currentUser;
    const uniquenessResult = await validatePhoneNumberUniqueness(phone, user?.uid);
    
    if (!uniquenessResult.isUnique) {
      setPhoneValidation({ isValid: false, message: uniquenessResult.message });
      setPhoneError(uniquenessResult.message);
    } else {
      setPhoneValidation({ isValid: true, message: 'Phone number is valid' });
      setPhoneError('');
    }
  };

  const handlePhoneNumberChange = (e) => {
    const phone = e.target.value;
    setPhoneNumber(phone);
    validatePhoneNumber(phone);
  };

  // Generate page numbers for pagination (like Google search)
  const generatePageNumbers = (currentPage, totalPages) => {
    const pages = [];
    const maxVisiblePages = 7; // Show up to 7 page numbers
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 4) {
        // Show first 5 pages + ellipsis + last page
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show first page + ellipsis + last 5 pages
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page + ellipsis + current page range + ellipsis + last page
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Load user data
  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged(async (user) => {
      if (user) {
        try {
          setLoading(true);
          
          // Load user profile data
          console.log('Loading user data for UID:', user.uid);
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          console.log('User document exists:', userDoc.exists());
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            console.log('User data loaded:', data);
            setUserData(data);
            
            // Set email from Firebase Auth user
            console.log('Firebase Auth user email:', user.email);
            setUserEmail(user.email || '');
            
            // Set form values
            setPseudo(data.pseudo || '');
            setFacebookLink(data.facebookLink || '');
            setPhoneNumber(data.phoneNumber || '');
            setMasterDuelId(data.masterDuelId || '');
            setEdoproPseudo(data.edoproPseudo || '');
            setStatsVisibility(data.statsVisibility || {
              matches: true,
              wins: true,
              losses: true,
              champion: true,
            });
            setNotificationsEnabled(data.notificationsEnabled !== false);
          } else {
            console.error('User document does not exist for UID:', user.uid);
            toast.error('Account data not found. Please contact support immediately.');
            setLoading(false);
            return;
          }

          // Load transaction history (DP purchases)
          try {
            console.log('Loading transaction history for UID:', user.uid);
            const transactionsQuery = query(
              collection(db, 'transactions'),
              where('userId', '==', user.uid),
              orderBy('timestamp', 'desc')
            );
            const transactionsSnapshot = await getDocs(transactionsQuery);
            console.log('Transaction query result:', transactionsSnapshot.docs.length, 'transactions found');
            const transactions = transactionsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setTransactionHistory(transactions);
            resetPagination(); // Reset pagination when data loads
          } catch (transactionError) {
            console.warn('Failed to load transaction history:', transactionError);
            console.warn('Transaction error details:', transactionError.message);
            // Don't fail the entire load if transactions fail
            setTransactionHistory([]); // Set empty array as fallback
          }

          // Load item purchase history
          try {
            console.log('Loading item purchase history for UID:', user.uid);
            const purchaseHistoryQuery = query(
              collection(db, 'users', user.uid, 'purchaseHistory'),
              orderBy('timestamp', 'desc')
            );
            const purchaseHistorySnapshot = await getDocs(purchaseHistoryQuery);
            console.log('Purchase history query result:', purchaseHistorySnapshot.docs.length, 'purchases found');
            const purchases = purchaseHistorySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setItemPurchaseHistory(purchases);
          } catch (purchaseError) {
            console.warn('Failed to load item purchase history:', purchaseError);
            console.warn('Purchase error details:', purchaseError.message);
            // Don't fail the entire load if purchases fail
            setItemPurchaseHistory([]); // Set empty array as fallback
          }

        } catch (error) {
          console.error('Error loading user data:', error);
          toast.error('Failed to load account data');
        } finally {
          setLoading(false);
        }
      } else {
        // No user signed in, redirect to login
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Save basic information
  const handleSaveBasicInfo = async () => {
    try {
      setSaving(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) return;

      // Validate phone number if provided
      if (phoneNumber && phoneNumber.trim()) {
        const uniquenessResult = await validatePhoneNumberUniqueness(phoneNumber, user.uid);
        if (!uniquenessResult.isUnique) {
          setPhoneError(uniquenessResult.message);
          toast.error(uniquenessResult.message);
          setSaving(false);
          return;
        }
      }

      // Use backup wrapper for basic info update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        pseudo,
        facebookLink,
        phoneNumber,
      }, 'profile_update', {
        fields: ['pseudo', 'facebookLink', 'phoneNumber'],
        previousValues: {
          pseudo: userData.pseudo,
          facebookLink: userData.facebookLink,
          phoneNumber: userData.phoneNumber
        },
        newValues: {
          pseudo,
          facebookLink,
          phoneNumber
        }
      });

      // Update pseudo in tournaments if changed
      if (pseudo !== userData.pseudo) {
        try {
          await updatePseudoInTournaments(pseudo);
        } catch (error) {
          console.warn('Failed to update pseudo in tournaments:', error);
        }
      }

      setUserData(prev => ({ ...prev, pseudo, facebookLink, phoneNumber }));
      toast.success(t('settings.basicInfoUpdated'));
    } catch (error) {
      console.error('Error saving basic info:', error);
      toast.error(t('settings.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  // Save gaming profiles
  const handleSaveGamingProfiles = async () => {
    try {
      setSaving(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) return;

      // Use backup wrapper for gaming profiles update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        masterDuelId,
        edoproPseudo,
      }, 'profile_update', {
        fields: ['masterDuelId', 'edoproPseudo'],
        previousValues: {
          masterDuelId: userData.masterDuelId,
          edoproPseudo: userData.edoproPseudo
        },
        newValues: {
          masterDuelId,
          edoproPseudo
        }
      });

      setUserData(prev => ({ ...prev, masterDuelId, edoproPseudo }));
      toast.success(t('settings.gamingProfilesUpdated'));
    } catch (error) {
      console.error('Error saving gaming profiles:', error);
      toast.error(t('settings.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  // Save privacy settings
  const handleSavePrivacySettings = async () => {
    try {
      setSaving(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) return;

      // Stats visibility is just a UI setting, no backup needed
      await updateDoc(doc(db, 'users', user.uid), {
        statsVisibility,
      });

      setUserData(prev => ({ ...prev, statsVisibility }));
      toast.success(t('settings.privacySettingsUpdated'));
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      toast.error(t('settings.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  // Save notification settings
  const handleSaveNotificationSettings = async () => {
    try {
      setSaving(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) return;

      // Use backup wrapper for notification settings update
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        notificationsEnabled,
      }, 'profile_update', {
        field: 'notificationsEnabled',
        previousValue: userData.notificationsEnabled,
        newValue: notificationsEnabled
      });

      setUserData(prev => ({ ...prev, notificationsEnabled }));
      toast.success(t('settings.notificationSettingsUpdated'));
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error(t('settings.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    setPasswordError('');
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    // Enforce password policy for password changes
    const shouldEnforce = shouldEnforcePasswordPolicy(false, true);
    if (shouldEnforce) {
      const validation = validatePassword(newPassword, userEmail, userData?.pseudo);
      if (!validation.isValid) {
        setPasswordError('Password does not meet security requirements. Please check the requirements below.');
        return;
      }
    }

    try {
      setChangingPassword(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user || !user.email) {
        setPasswordError('User not found');
        return;
      }

      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, newPassword);
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordChange(false);
      
      toast.success('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('New password is too weak');
      } else if (error.code === 'auth/requires-recent-login') {
        setPasswordError('Please log out and log back in before changing your password');
      } else {
        setPasswordError(error.message || 'Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };  

  // Cancel password change
  const handleCancelPasswordChange = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowPasswordChange(false);
  };

  

  if (loading) {
    return (
      <div className="account-settings-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('accountSettings.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-settings-page">
      <div className="settings-header">
        <div className="settings-header-content">
          <h1>{t('accountSettings.title')}</h1>
        </div>
      </div>

      <div className="settings-container">
        {/* Navigation Sidebar */}
        <div className="settings-nav">
          <button 
            className={`nav-item ${activeSection === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveSection('basic')}
          >
            <SettingsIcon name="basic" size={18} />
            {t('accountSettings.navigation.basicInfo')}
          </button>
          <button 
            className={`nav-item ${activeSection === 'gaming' ? 'active' : ''}`}
            onClick={() => setActiveSection('gaming')}
          >
            <SettingsIcon name="gaming" size={18} />
            {t('accountSettings.navigation.gaming')}
          </button>
          <button 
            className={`nav-item ${activeSection === 'security' ? 'active' : ''}`}
            onClick={() => setActiveSection('security')}
          >
            <SettingsIcon name="security" size={18} />
            {t('accountSettings.navigation.security')}
          </button>
          <button 
            className={`nav-item ${activeSection === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveSection('privacy')}
          >
            <SettingsIcon name="privacy" size={18} />
            {t('accountSettings.navigation.privacy')}
          </button>
          <button 
            className={`nav-item ${activeSection === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveSection('notifications')}
          >
            <SettingsIcon name="notifications" size={18} />
            {t('accountSettings.navigation.notifications')}
          </button>
          <button 
            className={`nav-item ${activeSection === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveSection('transactions')}
          >
            <SettingsIcon name="transactions" size={18} />
            {t('accountSettings.navigation.transactions')}
          </button>
          
        </div>

        {/* Content Area */}
        <div className="settings-content">
          {activeSection === 'basic' && (
            <div className="settings-section">
              <h2>{t('accountSettings.basicInfo.title')}</h2>
              <div className="form-group">
                <label>{t('accountSettings.basicInfo.pseudo')}</label>
                <input
                  type="text"
                  value={pseudo}
                  onChange={(e) => setPseudo(e.target.value)}
                  placeholder={t('accountSettings.basicInfo.pseudoPlaceholder')}
                />
              </div>
              <div className="form-group">
                <label>{t('accountSettings.basicInfo.facebookLink')}</label>
                <input
                  type="url"
                  value={facebookLink}
                  onChange={(e) => setFacebookLink(e.target.value)}
                  placeholder={t('accountSettings.basicInfo.facebookPlaceholder')}
                />
              </div>
              
              {/* Phone Number - Clickable Field or Locked */}
              <div className="form-group">
                <label>{t('accountSettings.basicInfo.phoneNumber')}</label>
                {phoneNumber ? (
                  // Phone number is set - show locked field
                  <div className="phone-number-display phone-locked">
                    <span className="phone-value">{phoneNumber}</span>
                    <span className="lock-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="11" width="14" height="10" rx="2" fill="#888888" stroke="#666666" strokeWidth="1.5"/>
                        <path d="M8 11V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V11" stroke="#666666" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="12" cy="16" r="1.5" fill="#444444"/>
                      </svg>
                    </span>
                  </div>
                ) : (
                  // No phone number - allow setting
                  <div 
                    className="phone-number-display clickable-field"
                    onClick={() => {
                      setTempPhoneNumber(phoneNumber || '');
                      setPhoneEditStep('edit');
                      setShowPhoneEditModal(true);
                    }}
                  >
                    <span className="phone-value">{t('accountSettings.basicInfo.phoneNumberSet')}</span>
                    <span className="edit-icon">✏️</span>
                  </div>
                )}
                <small style={{ color: '#888', marginTop: '5px', display: 'block' }}>
                  {phoneNumber ? t('accountSettings.basicInfo.phoneNumberLocked') : t('accountSettings.basicInfo.phoneNumberClick')}
                </small>
              </div>
              
              <div className="form-group">
                <label>{t('accountSettings.basicInfo.emailAddress')}</label>
                <div className="phone-number-display phone-locked">
                  <span className="phone-value">{userEmail}</span>
                  <span className="lock-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="11" width="14" height="10" rx="2" fill="#888888" stroke="#666666" strokeWidth="1.5"/>
                      <path d="M8 11V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V11" stroke="#666666" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="12" cy="16" r="1.5" fill="#444444"/>
                    </svg>
                  </span>
                </div>
                <small style={{ color: '#888', marginTop: '5px', display: 'block' }}>
                  {t('accountSettings.basicInfo.emailLocked')}
                </small>
              </div>
              <button 
                className="save-button"
                onClick={handleSaveBasicInfo}
                disabled={saving}
              >
                {saving ? t('accountSettings.saving') : t('accountSettings.saveChanges')}
              </button>
            </div>
          )}

          {activeSection === 'gaming' && (
            <div className="settings-section">
              <h2>{t('accountSettings.gaming.title')}</h2>
              <div className="form-group">
                <label>{t('accountSettings.gaming.masterDuelId')}</label>
                <input
                  type="text"
                  value={masterDuelId}
                  onChange={(e) => setMasterDuelId(e.target.value)}
                  placeholder={t('accountSettings.gaming.masterDuelIdPlaceholder')}
                />
                <small>{t('accountSettings.gaming.masterDuelIdHint')}</small>
              </div>
              <div className="form-group">
                <label>{t('accountSettings.gaming.edoproPseudo')}</label>
                <input
                  type="text"
                  value={edoproPseudo}
                  onChange={(e) => setEdoproPseudo(e.target.value)}
                  placeholder={t('accountSettings.gaming.edoproPseudoPlaceholder')}
                />
                <small>{t('accountSettings.gaming.edoproPseudoHint')}</small>
              </div>
              <div className="form-group">
                <label>{t('accountSettings.gaming.duelPoints')}</label>
                <input
                  type="number"
                  value={userData?.dp || 0}
                  disabled
                  className="disabled-input"
                />
                <small>{t('accountSettings.gaming.duelPointsHint')}</small>
              </div>
              <div className="form-group">
                <label>{t('accountSettings.gaming.ymePoints')}</label>
                <input
                  type="number"
                  value={userData?.YP || 0}
                  disabled
                  className="disabled-input"
                />
                <small>{t('accountSettings.gaming.ymePointsHint')}</small>
              </div>
              <button 
                className="save-button"
                onClick={handleSaveGamingProfiles}
                disabled={saving}
              >
                {saving ? t('accountSettings.saving') : t('accountSettings.saveChanges')}
              </button>
            </div>
          )}

          {activeSection === 'security' && (
            <div className="settings-section">
              <h2>{t('accountSettings.security.title')}</h2>
              
              {/* Password Section */}
              <div className="security-item">
                <h3>{t('accountSettings.security.passwordSection')}</h3>
                <p>{t('accountSettings.security.passwordDescription')}</p>
                
                {!showPasswordChange ? (
                  <button 
                    className="action-button"
                    onClick={() => setShowPasswordChange(true)}
                  >
                    {t('accountSettings.security.changePasswordButton')}
                  </button>
                ) : (
                  <div className="password-change-form">
                    <div className="form-group">
                      <label>{t('accountSettings.security.currentPassword')}</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t('accountSettings.security.currentPasswordPlaceholder')}
                        disabled={changingPassword}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>{t('accountSettings.security.newPassword')}</label>
                      <div className="password-input-container">
                        <input
                          type="password"
                          value={newPassword}
                          onChange={handleNewPasswordChange}
                          placeholder={t('accountSettings.security.newPasswordPlaceholder')}
                          disabled={changingPassword}
                        />
                        <button
                          type="button"
                          className="password-generator-btn"
                          onClick={() => setShowPasswordGenerator(!showPasswordGenerator)}
                          disabled={changingPassword}
                        >
                          {t('accountSettings.security.generate')}
                        </button>
                      </div>
                      
                      {/* Password Generator Modal */}
                      {showPasswordGenerator && (
                        <div className="password-generator-modal">
                          <div className="generator-content">
                            <h4>{t('accountSettings.security.passwordGenerator.title')}</h4>
                            <div className="generated-password-display">
                              <input
                                type="text"
                                value={generatedPassword}
                                readOnly
                                className="generated-password-input"
                              />
                              <button
                                type="button"
                                className="copy-password-btn"
                                onClick={() => {
                                  navigator.clipboard.writeText(generatedPassword);
                                  toast.success(t('accountSettings.security.passwordGenerator.copied'));
                                }}
                              >
                                {t('accountSettings.security.passwordGenerator.copy')}
                              </button>
                            </div>
                            <div className="generator-actions">
                              <button
                                type="button"
                                className="generate-new-btn"
                                onClick={generateNewPassword}
                              >
                                {t('accountSettings.security.passwordGenerator.generateNew')}
                              </button>
                              <button
                                type="button"
                                className="use-password-btn"
                                onClick={useGeneratedPassword}
                              >
                                {t('accountSettings.security.passwordGenerator.usePassword')}
                              </button>
                              <button
                                type="button"
                                className="close-generator-btn"
                                onClick={() => setShowPasswordGenerator(false)}
                              >
                                {t('accountSettings.security.passwordGenerator.cancel')}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {newPassword && (
                      <PasswordStrengthIndicator
                        password={newPassword}
                        email={userEmail}
                        username={userData?.pseudo}
                        showRequirements={true}
                        showSuggestions={true}
                      />
                    )}
                    
                    <div className="form-group">
                      <label>{t('accountSettings.security.confirmPassword')}</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('accountSettings.security.confirmPasswordPlaceholder')}
                        disabled={changingPassword}
                      />
                    </div>
                    
                    {passwordError && (
                      <div className="error-message">
                        {passwordError}
                      </div>
                    )}
                    
                    <div className="password-change-actions">
                      <button 
                        className="save-button"
                        onClick={handlePasswordChange}
                        disabled={changingPassword}
                      >
                        {changingPassword ? t('accountSettings.security.changing') : t('accountSettings.security.changePassword')}
                      </button>
                      <button 
                        className="action-button"
                        onClick={handleCancelPasswordChange}
                        disabled={changingPassword}
                      >
                        {t('accountSettings.security.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Two-Factor Authentication */}
              <div className="security-item">
                <h3>{t('accountSettings.security.twoFactorAuth')}</h3>
                <GoogleAuthenticatorSettings user={userData} />
              </div>

              
            </div>
          )}

          {activeSection === 'privacy' && (
            <div className="settings-section">
              <h2>{t('accountSettings.privacy.title')}</h2>
              <div className="form-group">
                <label>{t('accountSettings.privacy.statsVisibility')}</label>
                <p>{t('accountSettings.privacy.statsDescription')}</p>
                {Object.entries(statsVisibility).map(([key, value]) => (
                  <div key={key} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={key}
                      checked={value}
                      onChange={(e) => setStatsVisibility(prev => ({
                        ...prev,
                        [key]: e.target.checked
                      }))}
                    />
                    <label htmlFor={key}>
                      {t(`accountSettings.privacy.${key}`)}
                    </label>
                  </div>
                ))}
              </div>
              <button 
                className="save-button"
                onClick={handleSavePrivacySettings}
                disabled={saving}
              >
                {saving ? t('accountSettings.saving') : t('accountSettings.saveChanges')}
              </button>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div className="settings-section">
              <h2>{t('accountSettings.notifications.title')}</h2>
              <div className="form-group">
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="notifications"
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                  />
                  <label htmlFor="notifications" className="notification-label">
                    {t('accountSettings.notifications.enableNotifications')}
                  </label>
                </div>
                <small>{t('accountSettings.notifications.notificationsHint')}</small>
              </div>
              <button 
                className="save-button"
                onClick={handleSaveNotificationSettings}
                disabled={saving}
              >
                {saving ? t('accountSettings.saving') : t('accountSettings.saveChanges')}
              </button>
            </div>
          )}

          {activeSection === 'transactions' && (
            <div className="settings-section">
              <h2>{t('accountSettings.transactions.title')}</h2>
              
              {/* DP Purchase History */}
              <div className="transaction-subsection">
                <h3>{t('accountSettings.transactions.dpPurchases')}</h3>
                {(() => {
                  // Combine transactions from both sources
                  const allDPPurchases = [
                    ...transactionHistory,
                    ...itemPurchaseHistory.filter(purchase => purchase.collection === 'mvola')
                  ].sort((a, b) => {
                    const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp);
                    const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp);
                    return bTime - aTime; // Sort by newest first
                  });

                  const totalPages = getTotalPages(allDPPurchases, itemsPerPage);
                  const paginatedDPPurchases = getPaginatedData(allDPPurchases, dpPurchasePage, itemsPerPage);

                  return allDPPurchases.length > 0 ? (
                    <>
                      <div className="transaction-list">
                        {paginatedDPPurchases.map((transaction, index) => (
                          <div key={transaction.id || `mvola-${index}`} className="transaction-item dp-purchase">
                            <div className="transaction-info">
                              <div className="transaction-header">
                                <span className="transaction-type">
                                  {(transaction.type || transaction.itemName || 'DP Purchase').replace(' (Manual Approval)', '')}
                                </span>
                                <span className="transaction-status">
                                  {transaction.status || 'Completed'}
                                </span>
                              </div>
                              <div className="transaction-details">
                                <span className="transaction-amount">
                                  +{(() => {
                                    // For transactions from transactions collection
                                    if (transaction.amount || transaction.dpAmount) {
                                      return transaction.amount || transaction.dpAmount;
                                    }
                                    // For Mvola purchases from purchaseHistory, extract from itemName
                                    if (transaction.itemName && transaction.collection === 'mvola') {
                                      const match = transaction.itemName.match(/(\d+)\s*DP/i);
                                      return match ? match[1] : '0';
                                    }
                                    return '0';
                                  })()} DP
                                </span>
                                <span className="transaction-method">
                                  {transaction.method || (transaction.collection === 'mvola' ? 'MVola' : 'Unknown')}
                                </span>
                                <span className="transaction-date">
                                  {transaction.timestamp?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                </span>
                              </div>
                              {transaction.description && (
                                <div className="transaction-description">
                                  {transaction.description.replace(' (Manual Approval)', '')}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="pagination-controls">
                          <button 
                            className="pagination-btn prev-btn"
                            onClick={() => setDpPurchasePage(prev => Math.max(1, prev - 1))}
                            disabled={dpPurchasePage === 1}
                          >
                            {t('accountSettings.transactions.prev')}
                          </button>
                          
                          <div className="pagination-numbers">
                            {generatePageNumbers(dpPurchasePage, totalPages).map((page, index) => (
                              page === '...' ? (
                                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                              ) : (
                                <button
                                  key={page}
                                  className={`pagination-number ${dpPurchasePage === page ? 'active' : ''}`}
                                  onClick={() => setDpPurchasePage(page)}
                                >
                                  {page}
                                </button>
                              )
                            ))}
                          </div>
                          
                          <button 
                            className="pagination-btn next-btn"
                            onClick={() => setDpPurchasePage(prev => Math.min(totalPages, prev + 1))}
                            disabled={dpPurchasePage === totalPages}
                          >
                            {t('accountSettings.transactions.next')}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty-state">
                      <p>{t('accountSettings.transactions.noDPPurchases')}</p>
                      <button 
                        className="action-button"
                        onClick={() => navigate('/shop')}
                      >
                        {t('accountSettings.transactions.buyDP')}
                      </button>
                    </div>
                  );
                })()}
              </div>

          {/* Item Purchase History */}
          <div className="transaction-subsection">
            <h3>{t('accountSettings.transactions.itemPurchases')}</h3>
            {(() => {
              const filteredItemPurchases = itemPurchaseHistory.filter(purchase => purchase.collection !== 'mvola');
              const totalPages = getTotalPages(filteredItemPurchases, itemsPerPage);
              const paginatedItemPurchases = getPaginatedData(filteredItemPurchases, itemPurchasePage, itemsPerPage);

              return filteredItemPurchases.length > 0 ? (
                <>
                  <div className="transaction-list">
                    {paginatedItemPurchases.map((purchase) => (
                      <div key={purchase.id} className="transaction-item item-purchase">
                        <div className="transaction-info">
                          <div className="transaction-header">
                            <span className="transaction-type">
                              {purchase.itemName || 'Item Purchase'}
                            </span>
                            <span className="transaction-status">
                              {purchase.status || 'Completed'}
                            </span>
                          </div>
                          <div className="transaction-details">
                            <span className="transaction-amount">
                              -{purchase.totalCost || purchase.priceDP || purchase.dpAmount || 0} DP
                            </span>
                            <span className="transaction-method">
                              {(() => {
                                // Map collection names to display names
                                if (purchase.type === 'tournament_entry') {
                                  return t('accountSettings.transactions.methods.tournament');
                                }
                                if (purchase.collection === 'shopItems-v2') {
                                  return t('accountSettings.transactions.methods.ocg');
                                }
                                if (purchase.collection === 'shopItemsAccess-v2') {
                                  return t('accountSettings.transactions.methods.collection');
                                }
                                if (purchase.collection === 'shopItemsMisc-v2') {
                                  return t('accountSettings.transactions.methods.misc');
                                }
                                if (purchase.collection === 'shopItemsTCG-v2') {
                                  return t('accountSettings.transactions.methods.tcg');
                                }
                                if (purchase.collection === 'mvola') {
                                  return t('accountSettings.transactions.methods.mvola');
                                }
                                return purchase.collection || t('accountSettings.transactions.methods.shop');
                              })()}
                            </span>
                            <span className="transaction-date">
                              {purchase.timestamp?.toDate?.()?.toLocaleDateString() || 'N/A'}
                            </span>
                          </div>
                          {purchase.description && (
                            <div className="transaction-description">
                              {purchase.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="pagination-controls">
                      <button 
                        className="pagination-btn prev-btn"
                        onClick={() => setItemPurchasePage(prev => Math.max(1, prev - 1))}
                        disabled={itemPurchasePage === 1}
                      >
                        {t('accountSettings.transactions.prev')}
                      </button>
                      
                      <div className="pagination-numbers">
                        {generatePageNumbers(itemPurchasePage, totalPages).map((page, index) => (
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                          ) : (
                            <button
                              key={page}
                              className={`pagination-number ${itemPurchasePage === page ? 'active' : ''}`}
                              onClick={() => setItemPurchasePage(page)}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>
                      
                      <button 
                        className="pagination-btn next-btn"
                        onClick={() => setItemPurchasePage(prev => Math.min(totalPages, prev + 1))}
                        disabled={itemPurchasePage === totalPages}
                      >
                        {t('accountSettings.transactions.next')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <p>{t('accountSettings.transactions.noItemPurchases')}</p>
                  <button 
                    className="action-button"
                    onClick={() => navigate('/shop')}
                  >
                    {t('accountSettings.transactions.visitShop')}
                  </button>
                </div>
              );
            })()}
          </div>
            </div>
          )}

          
        </div>
      </div>
      
      {/* Phone Number Edit/Confirm Modal */}
      {showPhoneEditModal && (
        <div className="modal-overlay" onClick={() => {
          setShowPhoneEditModal(false);
          setPhoneEditStep('edit');
          setTempPhoneNumber('');
        }}>
          <div className="phone-edit-modal" onClick={(e) => e.stopPropagation()}>
            {phoneEditStep === 'edit' ? (
              // Edit Step
              <>
                <h2>{t('accountSettings.phoneModal.editTitle')}</h2>
                <p className="modal-description">
                  {t('accountSettings.phoneModal.editDescription')}
                </p>
                <div className="form-group">
                  <label>{t('accountSettings.phoneModal.phoneNumberLabel')}</label>
                  <input
                    type="tel"
                    value={tempPhoneNumber}
                    onChange={(e) => setTempPhoneNumber(e.target.value)}
                    placeholder={t('accountSettings.phoneModal.phoneNumberPlaceholder')}
                    maxLength={10}
                    className="phone-input"
                    autoFocus
                  />
                  <small>{t('accountSettings.phoneModal.phoneNumberFormat')}</small>
                </div>
                <div className="modal-buttons-row">
                  <button 
                    className="save-btn"
                    onClick={() => {
                      if (!tempPhoneNumber || !validatePhoneNumberFormat(tempPhoneNumber)) {
                        toast.error(t('accountSettings.phoneModal.invalidFormat'));
                        return;
                      }
                      setPhoneEditStep('confirm');
                    }}
                    disabled={!tempPhoneNumber}
                  >
                    {t('accountSettings.phoneModal.save')}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={() => {
                      setShowPhoneEditModal(false);
                      setPhoneEditStep('edit');
                      setTempPhoneNumber('');
                    }}
                  >
                    {t('accountSettings.phoneModal.cancel')}
                  </button>
                </div>
              </>
            ) : (
              // Confirm Step
              <>
                <div className="modal-icon">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 20h20L12 2z" fill="#ffa726" stroke="#ff9800" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 9v4M12 17h.01" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2>{t('accountSettings.phoneModal.confirmTitle')}</h2>
                <p className="confirmation-text">
                  {t('accountSettings.phoneModal.confirmDescription')} <strong>{tempPhoneNumber}</strong>
                </p>
                <p className="warning-text">
                  {t('accountSettings.phoneModal.warning')}
                </p>
                <div className="modal-buttons-row">
                  <button 
                    className="confirm-btn"
                    onClick={async () => {
                      try {
                        const auth = getAuth();
                        const user = auth.currentUser;
                        if (!user) {
                          toast.error(t('accountSettings.phoneModal.notAuthenticated'));
                          return;
                        }

                        // Save to Firestore immediately
                        const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
                        await updateUserProfileWithBackup(user.uid, {
                          phoneNumber: tempPhoneNumber,
                        }, 'phone_number_update', {
                          field: 'phoneNumber',
                          previousValue: phoneNumber,
                          newValue: tempPhoneNumber
                        });

                        // Update local state
                        setPhoneNumber(tempPhoneNumber);
                        setUserData(prev => ({ ...prev, phoneNumber: tempPhoneNumber }));
                        setShowPhoneEditModal(false);
                        setPhoneEditStep('edit');
                        setTempPhoneNumber('');
                        toast.success(t('accountSettings.phoneModal.savedSuccess'));
                      } catch (error) {
                        console.error('Error saving phone number:', error);
                        toast.error(t('accountSettings.phoneModal.savedError'));
                      }
                    }}
                  >
                    {t('accountSettings.phoneModal.confirm')}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={() => {
                      setShowPhoneEditModal(false);
                      setPhoneEditStep('edit');
                      setTempPhoneNumber('');
                    }}
                  >
                    {t('accountSettings.phoneModal.cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Back Button at Bottom */}
      <div className="back-button-container">
        <button className="back-button" onClick={() => navigate(-1)}>
           {t('accountSettings.backButton')}
        </button>
      </div>
      
      
    </div>
  );
};

export default AccountSettingsPage;
